import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { SessionKeys } from 'src/app/shared/models/SessionKeys';
import { SessionService } from '../../../core/services/session.service';
import { BeEvents } from '../../../shared/models/BackendEventLogExports';
import { CaptureChildDetails, UpdateCaptureChildDetails } from '../../../shared/models/CaptureChildDetails';
import { ContactDetails, TOUResponseModel, saveContactDetails, saveTermsOfUseDetails, saveUserLanguage } from '../../../shared/models/ContactDetails';
import { ContractCancellationStatus, ContractDataTransactionViewModel, ContractDataViewModel, ContractModel, CustomerContractDetailsViewModel, StopLossTransactionData } from '../../../shared/models/Contract';
import { DistributionChannel } from '../../../shared/models/distributionChannelType';
import { DynamicHeader } from '../../../shared/models/DynamicHeader';
import { ProductCodeRange, ProductModules } from '../../../shared/models/LeadProducts';
import { NotificationsResponseModel, ScreenType } from '../../../shared/models/notifications';
import { EligibilityReason, IsSubscriptionAllowedReqObj, IsSubscriptionAllowedResponse, PersonAndPreferences, Preferences } from '../../../shared/models/PersonAndPreferences';
import { PersonDetails } from '../../../shared/models/PersonDetailsModel';
import { RecurrentAmountModifier } from '../../../shared/models/RecurrentAmount';
import { SepaDeeplinkModel } from '../../../shared/models/SepaDeeplinkModel';
import { NewSepaMandate } from '../../../shared/models/SepaMandateViewModel';
import { StartParameter } from '../../../shared/models/StartParameter';
import { ScreenTypeCode, SurveyModel } from '../../../shared/models/SurveyModel';
import { surveySectionId } from '../../../shared/models/SurveySectionId';
import { SurveySimulationModel } from '../../../shared/models/SurveySimulationModel';
import { CheckCredentials, CheckCredentialsResponse, UserLoginRequestModel, UserLoginResponseModel } from '../../../shared/models/UserLogin';
import { RouteNames } from '../../../shared/routing/app-routing.constants';
import { BackendEventLogService } from '../../../shared/services/backend-event-log.service';
import { FactoryhelperService } from '../../../shared/services/factoryHelper.service';
import { HelperService } from '../../../shared/services/helper.service';
import { UserSimulationService } from '../../simulation/services/user-simulation.service';
import { UserSurveyService } from '../../survey/services/user-survey.service';
import { SurveyParams } from './../../../shared/models/survey-params';


interface PartnerAllowedViewModel {
  IsClientAllowedForPartner: boolean;
  PartnerCallBackUrlFR?: string;
  PartnerCallBackUrlNL?: string;
  PartnerName?: string;
}
@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  private isFirstTimeSurvey = false;
  private pendingSurveyList: SurveySimulationModel[];
  apiUrl: string;
  private customerDetails: PersonDetails;
  private dynamicHeader: DynamicHeader;
  private preferencesData: PersonAndPreferences;
  private selectedSurvey: SurveySimulationModel;
  currentSepaDetails: NewSepaMandate;
  private contactDate: ContactDetails

  private successMessage: string;
  private editContactSuccessMessage: string;

  FINANCIAL_CONSTANTS = {
    ONE_SHOT_MAX_LIMIT: 60000,
    ONE_SHOT_MIN_LIMIT: 10,
    MONTHLY_MAX_LIMIT: 1125,
    MONTHLY_MIN_LIMIT: 10
  };
  private contracts: ContractModel[];
  isSimulationQuotaOver = false;
  maxSimulationsForClient = 15;
  maxZennieSimulationForClient = 1;
  ChildDetailsCaptured$ = new Subject<boolean>();
  isOnlinePaymentActive: boolean;
  isSubscriptionAllowedReason: IsSubscriptionAllowedResponse;
  isResumeSimulation: boolean;
  private broadcastData$ = new BehaviorSubject<any>(null);


  constructor(
    private readonly router: Router,
    private readonly userSurveyService: UserSurveyService,
    private readonly agTranslateService: AgTranslateService,
    private readonly factoryhelperService: FactoryhelperService,
    private readonly session: SessionService,
    private readonly userSimulationService: UserSimulationService,
    private readonly helper: HelperService,
    private readonly backendEventLogging: BackendEventLogService,
  ) {
    this.apiUrl = AgAppConfigService.get<string>('applicativeApiUrl');
  }

  getCustomerDetailsFromDB(personId: number, partnerCode?: number, partnerToken?: string): Observable<PersonDetails> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/manageuser/retrievepersondetails/personid/${personId}`;
    // if (partnerCode || partnerToken){
    let apiParams = new HttpParams();
    if (partnerToken) {
      apiParams = apiParams.append('partnerToken', encodeURIComponent(partnerToken));
    }
    apiParams = apiParams.append('partnerCode', partnerCode ? partnerCode.toString() : '0');
    return this.factoryhelperService.get(url, { params: apiParams }).pipe(map((x: { Result: PersonDetails }) => x.Result));
  }

  setCustomerDetails(data: PersonDetails): void {
    this.customerDetails = data;
  }

  getCustomerDetails(): PersonDetails {
    return this.customerDetails;
  }

  setSelectedSurvey(data: SurveySimulationModel): void {
    this.selectedSurvey = data;
  }

  getSelectedSurvey(): SurveySimulationModel {
    return this.selectedSurvey;
  }

  isChildDetailsCaptured(): Observable<boolean> {
    return this.ChildDetailsCaptured$.asObservable();
  }

  // checkSurveyFlow(personId: number) { // this method is being called after successful updation of user details
  //   this.userSurveyService.getSimulation(personId).pipe(
  //     switchMap((data: SurveySimulationModel[]) => {
  //       if (!data.length) { // checking if the user has any pending survey or not
  //         this.isFirstTimeSurvey = true;
  //         return this.createNewSimulation(personId);
  //       } else {
  //         this.isFirstTimeSurvey = false;
  //         return of(data);
  //       }
  //     })
  //   ).subscribe(data => this.determineNextFlow(data));
  // }

  createNewSimulation(personId: number, productCodeRange: number): Observable<SurveySimulationModel[]> {
    const simulationModel = new SurveySimulationModel();
    simulationModel.PersonId = Number(personId);
    simulationModel.PartnershipId = Number(this.session.getUserParams()?.partnercode);
    simulationModel.PartnerToken = this.session.getUserParams()?.clientExtRef;
    const module = productCodeRange === Number(ProductCodeRange.Uppie) ? ProductModules.Uppie : ProductModules.Zennie.QuoteCreation;
    return this.userSurveyService.createSimulation(simulationModel, productCodeRange, module).pipe(
      map(res => {
        res.sort((a, b) => {
          return a.SimulationId < b.SimulationId ? -1 : 1;
        });
        return res;
      })
    );
  }

  createQuoteAfterRegistration(personId: number, productCodeRange: number): Observable<SurveySimulationModel[]> {
    return this.userSurveyService.getSimulation(personId).pipe(
      switchMap((data: SurveySimulationModel[]) => {
        if (!data.length) { // checking if the user has any pending survey or not
          // this.isFirstTimeSurvey = true;
          return this.createNewSimulation(personId, productCodeRange);
        } else {
          // this.isFirstTimeSurvey = false;
          return EMPTY;
        }
      })
    );
  }

  // determineNextFlow(data: SurveySimulationModel[]) {
  //   if (!this.isFirstTimeSurvey) {
  //     this.pendingSurveyList = data;
  //     void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
  //   } else {
  //     this.startSurvey(data[0]);
  //   }
  // }

  getPendingSurvey(): SurveySimulationModel[] {
    return this.pendingSurveyList;
  }

  setPendingSurvey(data: SurveySimulationModel[]): void {
    this.pendingSurveyList = data;
  }

  startSimulation(flow: string, isFatcaScreenBackButtonHidden?: boolean): void {
    if (flow === 'createQuote') {
      const personDetails = this.getCustomerDetails();
      if (this.session.getUserParams().partnercodeRange === ProductCodeRange.Uppie || this.session.getUserParams().partnercodeRange === ProductCodeRange.Yongo) {
        if (this.session.getUserParams()?.partnercode) { // creating a simulation only if partner code is there
          this.createNewSimulation(Number(localStorage.personId), this.session.getUserParams().partnercodeRange).subscribe(res => {
            this.helper.clearSession(SessionKeys.startSurveyKey); // removing the session variable for starting simulation
            this.setPendingSurvey(res); // resetting the pending simulation list
            // Incase of uppie flow, user will start the survey after quote creation
            this.session.clearBeneficiaryChild();
            this.startSurvey(res[res.length - 1], isFatcaScreenBackButtonHidden);
            sessionStorage.setItem('selectedProductTypeCode', null);
          });
        } else {
          console.error('Without a partner simuation/survey cannot be started');
          void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
        }
      }
      else if (this.session.getUserParams().partnercodeRange === ProductCodeRange.Zennie) {
        void this.router.navigate([`${RouteNames.needs}/${RouteNames.needsShowIntro}`]);
      }
      else {
        void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
      }
    }
    else if (flow === 'survey') { // if this is a survey flow then user should go back to QnA and not dashboard
      const surveyParams = (sessionStorage.getItem('userSurvey') as SurveyParams) ? (JSON.parse(atob(sessionStorage.getItem('userSurvey'))) as SurveyParams) : null;
      const ongoingSimulationId = Number(surveyParams?.simulationId);
      if (this.getPendingSurvey()?.length) {
        const ongoingSurvey = this.getPendingSurvey().find(x => x.SimulationId === ongoingSimulationId);
        ongoingSurvey.SectionId = (surveyParams?.startingStep as surveySectionId) === surveySectionId.fatca ? surveySectionId.fatca : ongoingSurvey.SectionId
        if (ongoingSurvey.ProductRange === ProductCodeRange.Uppie || ongoingSurvey.ProductRange === ProductCodeRange.Yongo) {
          this.userSurveyService.restoreSurveySessionValues(ongoingSurvey.SimulationId, ongoingSurvey.SectionId).subscribe(_res => {
            if (ongoingSurvey.SectionId === this.userSurveyService.onboardingSectionId) {
              void this.router.navigate(['/' + this.userSurveyService.onboardingSectionId + '/' + RouteNames.onBoarding + '/' + RouteNames.onBoardingIntro], { queryParams: { resume: 'true' } });
            } else {
              this.startSurvey(ongoingSurvey);
            }
          });
        } else if (ongoingSurvey.ProductRange === ProductCodeRange.Zennie) {
          console.log("zennie check");
        }

      } else { // but if page is refreshed and there is no survey data then user will be taken to dashboard
        this.userSurveyService.getSimulation(Number(localStorage.personId)).subscribe(res => {
          this.setPendingSurvey(res);
          if (res.length) {
            if (ongoingSimulationId) {
              const ongoingSurvey = res.find(x => x.SimulationId === ongoingSimulationId);
              ongoingSurvey.SectionId = (surveyParams?.startingStep as surveySectionId) === surveySectionId.fatca ? surveySectionId.fatca : ongoingSurvey.SectionId
              if (ongoingSurvey.ProductRange === ProductCodeRange.Uppie || ongoingSurvey.ProductRange === ProductCodeRange.Yongo) {
                this.startSurvey(ongoingSurvey);
              } else if (ongoingSurvey.ProductRange === ProductCodeRange.Zennie) {
                console.log("zennie check");
              }
            } else { // fallback scenario
              // this.customerService.startSurvey(this.customerService.getPendingSurvey()[0], true);
              void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
            }
          } else {
            void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
          }
        });
      }
    }
    else {
      void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
    }
  }

  startSurvey(survey: SurveySimulationModel, isFatcaScreenBackButtonHidden?: boolean): void {
    const isStartOver = survey.SectionId === Number(surveySectionId.fatca);
    const isResume = survey.SectionId !== Number(surveySectionId.fatca);
    this.checkForPartner(Number(localStorage.personId), survey.PartnershipId, survey.ProductRange).subscribe(res => {
      if (res.IsClientAllowedForPartner) {
        this.userSurveyService.clearQuestionCache(); //  making the question cache empty before starting/resuming a simulation

        const surveyId = survey.Survey;
        const simulationId = survey.SimulationId;

        sessionStorage.userSurvey = btoa(JSON.stringify({
          surveyId: survey.Survey,
          simulationId: survey.SimulationId,
          startingStep: isStartOver ? surveySectionId.fatca : survey.SectionId,
          partnerId: survey.PartnershipId,
          surveyCode: survey.Survey,
          partnerCodeRange: survey.ProductRange
        })
        );
        const sectionId = isStartOver ? surveySectionId.fatca : survey.SectionId;
        let questionToBeNavigatedTo: number;
        let sectionToBeNavigatedTo: number;
        let moduleToBeNavigatedTo: number;

        const userParams = this.session.getUserParams();
        userParams.distributionChannel = DistributionChannel.AG;
        userParams.partnercode = survey.PartnershipId;
        this.session.setModifiedUserParam(userParams);

        this.userSimulationService.getStartParametersForValidation(survey.PartnershipId, survey.ProductRange).pipe(map((x: { Result: StartParameter }) => x.Result)).subscribe((d: StartParameter) => {
          if (d) {
            this.session.setHeaderContext(null, null, null, d.PartnerLogo, d.PartnerLinkFR, d.PartnerLinkNL);
            this.userSurveyService.getQuestionsFromAPI(surveyId, simulationId, sectionId).subscribe(

              (result: SurveyModel[]) => {

                if (result.length) {
                  this.userSurveyService.setQuestions(result);

                  const firstQuestion = result.find(({ IsFirstQuestionFromSection }) => {
                    return IsFirstQuestionFromSection === true;
                  });
                  questionToBeNavigatedTo = firstQuestion.QuestionId;
                  sectionToBeNavigatedTo = firstQuestion.SectionId;
                  moduleToBeNavigatedTo = firstQuestion.SurveyCode;
                  if (questionToBeNavigatedTo >= 36 && questionToBeNavigatedTo <= 59) {
                    let queryParams = null;
                    if (isResume) {
                      queryParams = { resume: 'true' };
                    }
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, max-len
                    void this.router.navigate([`${moduleToBeNavigatedTo}/${surveySectionId.knowledgeIntro}/${RouteNames.surveyModule}/${RouteNames.questions}/${this.userSurveyService.KnowledgeIntroQuestionId}/${RouteNames.knowledgeIntro}`], { queryParams });
                  }
                  else {
                    if (firstQuestion.ScreenTypeCode === ScreenTypeCode.StaticScreen) {
                      if (firstQuestion.StaticScreenId === RouteNames.intro) {
                        void this.router.navigate([`${moduleToBeNavigatedTo}/${sectionToBeNavigatedTo}/${RouteNames.surveyModule}/${RouteNames.questions}/${firstQuestion.NextQuestionId}`]);
                      }
                      else {
                        let queryParams: { resume: string } = null;
                        if (isResume) {
                          queryParams = { resume: 'true' };
                        }
                        void this.router.navigate([`${moduleToBeNavigatedTo}/${sectionToBeNavigatedTo}/${RouteNames.surveyModule}/${RouteNames.questions}/${firstQuestion.QuestionId}/${firstQuestion.StaticScreenId}`],
                          { queryParams });
                      }
                    }
                    else {
                      let queryParams: { isSubscription: string } = null;
                      if (sectionToBeNavigatedTo === Number(surveySectionId.fatca)) {
                        this.userSurveyService.makeSectionInvalid(<SurveyParams>JSON.parse(atob(sessionStorage.userSurvey as string)), sectionToBeNavigatedTo);
                        if (isFatcaScreenBackButtonHidden) {
                          queryParams = { isSubscription: 'true' }
                        }
                      }
                      void this.router.navigate([`${moduleToBeNavigatedTo}/${sectionToBeNavigatedTo}/${RouteNames.surveyModule}/${RouteNames.questions}/${questionToBeNavigatedTo}`], { queryParams });
                    }
                  }
                } else {
                  // fallback mechanism
                  console.error("No questions found")
                  void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
                }
              }
            );
          }
        });
      } else {
        void this.router.navigate([`${RouteNames.blocked}/${survey.ProductRange === ProductCodeRange.Uppie ? RouteNames.InvalidTokenUppie : RouteNames.InvalidTokenZennie}/true`],
          {
            queryParams:
            {
              callback_FR: res.PartnerCallBackUrlFR,
              callback_NL: res.PartnerCallBackUrlNL,
              partner: res.PartnerName, invalid: true
            }
          }
        );
      }
      return;
    });


  }


  getDynamicHeaderFromDB(personId: number): Observable<DynamicHeader> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcommon/getdynamicheaderdetails/${personId}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: DynamicHeader }) => x.Result));
  }

  getContractsFromDB(personId: number): Observable<ContractModel[]> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcustomerzone/person/${personId}/getcustomercontracts`;
    return this.factoryhelperService.get(url).pipe(
      map((x: { Result: ContractModel[] }) => x.Result),
      tap((res: ContractModel[]) => {
        this.contracts = res;
      })
    );
    // return of([]);
  }

  getContractDetailsFromDB(planId: number, branchId: string): Observable<CustomerContractDetailsViewModel> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcustomerzone/plan/${planId}/branch/${branchId}/getcustomercontractdetails`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: CustomerContractDetailsViewModel }) => x.Result));
  }

  getContractCancellationStatus(planId: number): Observable<ContractCancellationStatus> {
    const culture = this.agTranslateService.currentLang;
    const apiParams = new HttpParams({ fromObject: { planId: planId.toString() } });
    const url = `${this.apiUrl}/${culture}/silcustomerzone/contractcancellationstatus`;
    return this.factoryhelperService.get(url, { params: apiParams }).pipe(map((x: { Result: ContractCancellationStatus }) => x.Result));
  }

  preCancelContract(planId: number): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcustomerzone/precancelcontract/${planId}`;
    return this.factoryhelperService.post(url, null).pipe(map((x: { Result: boolean }) => x.Result));
  }

  getTransactionsDetailsFromDB(contractNumber: string): Observable<ContractDataViewModel> {
    const culture = this.agTranslateService.currentLang;
    const apiParams = new HttpParams({ fromObject: { ContractNumber: contractNumber } });
    const url = `${this.apiUrl}/${culture}/silcontract/contractdata`;
    return this.factoryhelperService.get(url, { params: apiParams }).pipe(map((x: { Result: ContractDataViewModel }) => x.Result));
  }
  getStopLossTransactionDetails(stopLossTransactionId: number): Observable<StopLossTransactionData> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/stopLosstransaction/${stopLossTransactionId}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: StopLossTransactionData }) => x.Result));
  }

  getTaxDetails(contractNumber: string, contributionId: number): Observable<ContractDataTransactionViewModel[]> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/contractdatadetails`;
    const contractDataRequestViewModel = {
      ContractNumber: contractNumber,
      ContributionId: contributionId
    };
    return this.factoryhelperService.post(url, contractDataRequestViewModel).pipe(map((x: { Result: ContractDataTransactionViewModel[] }) => x.Result));
  }

  getContractReferenceForNewSepa(planId: number, personId: number): Observable<SepaDeeplinkModel> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/manageuser/person/${personId}/plan/${planId}/validateperson`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: SepaDeeplinkModel }) => x.Result));
  }

  getPersonPreferenceDataFromDB(personId: number): Observable<PersonAndPreferences> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/getUserconsentpreferences/personid/${personId}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: PersonAndPreferences }) => x.Result));
  }

  updatePersonPreferenceData(preferences: Preferences): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/updateuserconsentpreferences`;
    return this.factoryhelperService.post(url, preferences).pipe(map((x: { Result: boolean }) => x.Result));
  }

  private checkForPartner(personId: number, partnerCode: number, productRangecode: number): Observable<PartnerAllowedViewModel> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silpartnerintegration/person/${personId}/partner/${partnerCode}/product/${productRangecode}/isclientallowedforpartner `;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: PartnerAllowedViewModel }) => x.Result));
  }

  setPreferenceData(preferencesData: PersonAndPreferences): void {
    this.preferencesData = preferencesData;
  }

  getPreferenceData(): PersonAndPreferences {
    return this.preferencesData;
  }

  setContactData(contactData: ContactDetails): void {
    this.contactDate = contactData;
  }

  getContactData(): ContactDetails {
    return this.contactDate;
  }

  getAllDocuments(ContractReferenceNumber: string): Observable<unknown> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcustomerzone/contractreference/${ContractReferenceNumber}/documents`;
    return this.factoryhelperService.get<{ Result: unknown }>(url).pipe(map(x => x.Result));
  }

  setHeaderDetails(headerData: DynamicHeader): void {
    this.dynamicHeader = headerData;
  }

  getHeaderDetails(): DynamicHeader {
    return this.dynamicHeader;
  }

  getContracts(): ContractModel[] {
    return this.contracts;
  }

  setContracts(data: ContractModel[]): void {
    this.contracts = data;
  }

  sendSMSOTP(personId: number, isResend: boolean): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcommon/sendotpcode`;
    return this.factoryhelperService.post(url, { PersonId: personId, IsResend: isResend }).pipe(map((x: { Result: boolean }) => x.Result));
  }

  validateAndModifyAmount(modifyAmountData: RecurrentAmountModifier): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/updaterecurrentpaymentamount`;
    return this.factoryhelperService.post(url, modifyAmountData).pipe(map((x: { Result: boolean }) => x.Result));
  }

  stopRecurrentPayment(stopRecurrentAmount: RecurrentAmountModifier): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/updaterecurrentpaymentamount`;
    return this.factoryhelperService.post(url, stopRecurrentAmount).pipe(map((x: { Result: boolean }) => x.Result));
  }

  setEditSuccessMessage(message: string): void {
    this.successMessage = message;
  }

  getEditSuccessMessage(): string {
    const message = this.successMessage;
    this.successMessage = null;
    return message;
  }
  createRecurrentPayment(newSepaMandate: NewSepaMandate): Observable<{ EsignatureId: string }> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/processordersepamandate`;
    return this.factoryhelperService.post(url, newSepaMandate).pipe(map((x: { Result: { EsignatureId: string } }) => x.Result));
    //return of({EsignatureId: '13f27a17-1a65-4722-884d-c53989b81fc0'});
    //return of({EsignatureId: '13f27a17-1a65-4722-884d-c53989b81fc0'});
  }

  get getCurrentSepaDetails(): NewSepaMandate {
    return this.currentSepaDetails
  }

  set setCurrentSepaDetails(data: NewSepaMandate) {
    this.currentSepaDetails = data;
  }

  verifyUserContactDetailsDoesNotExist(CheckCredentials: CheckCredentials): Observable<CheckCredentialsResponse> {
    const culture = this.agTranslateService.currentLang;
    let apiParams = new HttpParams().set('personId', CheckCredentials.PersonId.toString());

    if (CheckCredentials.EmailAddress) {
      apiParams = apiParams.set('emailAddress', CheckCredentials.EmailAddress);
    }

    if (CheckCredentials.PhoneNumber) {
      apiParams = apiParams.set('phoneNumber', CheckCredentials.PhoneNumber);
    }
    const url = `${this.apiUrl}/${culture}/useraccount/checkifcontactdetailsexistinlead`;
    return this.factoryhelperService.get(url, { params: apiParams }).pipe(map((x: { Result: CheckCredentialsResponse }) => x.Result));
  }
  updateMandate(esignId: string, esignStatus: boolean, isModifyFlow: boolean): Observable<{ PlanId: number, Status: boolean }> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silproductonboarding/esign/${esignId}/status/${esignStatus}/updatemandatedetails`;
    let apiParams = new HttpParams();
    isModifyFlow ? apiParams = apiParams.append('IsIbanChange', 'true') : apiParams = apiParams.append('IsIbanChange', 'false');
    return this.factoryhelperService.patch(url, null, { params: apiParams }).pipe(map((x: { Result: { PlanId: number, Status: boolean } }) => x.Result));
  }
  updateBankAccount(newAccountDetail: { PersonId: number, ContractExtRef: string, Iban: string, PlanId: number }): Observable<{ EsignatureId: string }> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/changebankaccount`;
    return this.factoryhelperService.post(url, newAccountDetail).pipe(map((x: { Result: { EsignatureId: string } }) => x.Result));
  }

  setEditContactSuccessMessage(message: string): void {
    this.editContactSuccessMessage = message;
  }

  getEditContactSuccessMessage(): string {
    const message = this.editContactSuccessMessage;
    this.editContactSuccessMessage = null;
    return message;
  }

  getChildDetails(personId: number): Observable<CaptureChildDetails[]> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/manageuser/getadultchildrendetailsforyongoone/personid/${personId}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: CaptureChildDetails[] }) => x.Result));
  }

  saveAdultChildDetails(data: UpdateCaptureChildDetails): Observable<unknown> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/manageuser/saveadultchilddetails`;
    return this.factoryhelperService.post(url, data);
  }
  // #region OnlinePayment - Feature Flag
  getOnlinePaymentActivationStatusfromDB(): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/onlinepayment/IsOnlinePaymentActive`;
    return this.factoryhelperService.get(url);
  }

  get getOnlinePaymentActivationStatus(): boolean {
    return this.isOnlinePaymentActive;
  }

  set setOnlinePaymentActivationStatus(status: boolean) {
    this.isOnlinePaymentActive = status;
  }
  // #endregion
  userLogin(data: UserLoginRequestModel): Observable<UserLoginResponseModel> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/loginuser?uci=${data.Uci}&processCode=${encodeURIComponent(data.ProcessCd)}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: UserLoginResponseModel }) => x.Result));
  }

  checkIsSubscriptionAllowed(data: IsSubscriptionAllowedReqObj): Observable<IsSubscriptionAllowedResponse> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/silcontract/issubscriptionallowed?uci=${data.Uci}&productRangecode=${encodeURIComponent(data.ProductRangeCd)}`;
    return this.factoryhelperService.get(url).pipe(map((x: { Result: IsSubscriptionAllowedResponse }) => x.Result));
  }

  get getSubscriptionAllowedReason(): IsSubscriptionAllowedResponse {
    return this.isSubscriptionAllowedReason;
  }

  set setSubscriptionAllowedReason(status: IsSubscriptionAllowedResponse) {
    this.isSubscriptionAllowedReason = status;
  }

  ageCountryCheck(data: EligibilityReason, productRangeCd: ProductCodeRange): void {
    if (data === EligibilityReason.InvalidAge) {
      this.backendEventLogging.logBackendEvents(BeEvents.InvalidAge);
      if (productRangeCd === ProductCodeRange.Uppie) {
        void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedAge}`]);
      } else {
        void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedAgeRangeYongo}`]);
      }
    } else if (data == EligibilityReason.NotBelgianResident) {
      this.backendEventLogging.logBackendEvents(BeEvents.NotBelgianResident);
      if (productRangeCd === ProductCodeRange.Uppie) {
        void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedCountry}`]);
      } else {
        void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedCountryRangeYongo}`]);
      }
    } else if ((data === EligibilityReason.NissExpired) || (data === EligibilityReason.NissExpiryMissing)) {
      if (data === EligibilityReason.NissExpired) {
        this.backendEventLogging.logBackendEvents(BeEvents.NissExpired);
      } else if (data === EligibilityReason.NissExpiryMissing) {
        this.backendEventLogging.logBackendEvents(BeEvents.NissExpiryMissing);
      }
    }
  }

  getTermsOfUseConsent(personId: number): Observable<TOUResponseModel> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/gettermsofuseconsent/person/${personId}`;
    return this.factoryhelperService.get<{ Result: TOUResponseModel }>(url).pipe(map(x => x?.Result))
  }

  saveTermsOfUseConsent(data: saveTermsOfUseDetails): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/savetermsofuseconsent`;
    return this.factoryhelperService.post(url, data).pipe(map((x: { Result: boolean }) => x.Result));
  }

  saveContactDetails(data: saveContactDetails): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/savecontactdetails`;
    return this.factoryhelperService.post(url, data).pipe(map((x: { Result: boolean }) => x.Result));
  }

  get getResumeSimulation(): boolean {
    return this.isResumeSimulation;
  }

  set setResumeSimulation(flag: boolean) {
    this.isResumeSimulation = flag;
  }

  saveUserLanguage(data: saveUserLanguage): Observable<boolean> {
    const culture = this.agTranslateService.currentLang;
    const url = `${this.apiUrl}/${culture}/useraccount/updateuserlanguagepreference`;
    return this.factoryhelperService.post(url, data).pipe(map((x: { Result: boolean }) => x.Result));
  }

  iContinue(survey: SurveySimulationModel) {
    this.helper.clearSession(SessionKeys.startSurveyKey);
    this.setSelectedSurvey(survey);
    const userParams = this.session.getUserParams();
    userParams.partnercodeRange = survey.ProductRange;
    this.session.setModifiedUserParam(userParams);
    void this.router.navigate([`/${RouteNames.customerZone}/${RouteNames.iContinue}`]);
  }

  fetchNotificationMessages(uci: string, screenType: ScreenType) {
    const culture = this.agTranslateService.currentLang;
    const apiParams = new HttpParams().set('uci', uci).set('screenType', screenType.toString());
    const url = `${this.apiUrl}/${culture}/silcustomerzone/notification`;
    return this.factoryhelperService.get<{ Result: NotificationsResponseModel }>(url, { params: apiParams }).pipe(map(x => x?.Result), tap(result => {
      if (result) {
        this.broadcastData$.next(result);
      }
    }));
  }
  getBroadcastData() {
    return this.broadcastData$.asObservable();
  }

}
